import { useEffect, useState, useRef } from 'react';
import './Rbac.css';
import Layout from '../../../components/Layout';
import AdminNavbar from '../../../components/Admin/AdminNavbar/AdminNavbar';
import {
  Box,
  Typography,
  Avatar,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Modal,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Input,
  Popover,
  Menu,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import ColoredFilterIcon from '../../../assets/Admin/RequestImages/colored-filter-icon.svg';
import FilterIcon from '../../../assets/Admin/RequestImages/filter-icon.svg';
import DeleteIcon from '../../../assets/Admin/Banner/delete.svg';
import EditIcon from '../../../assets/Admin/Banner/edit.svg';
import closeIcon from '../../../assets/Admin/RequestImages/close-icon.svg';
import rejectIcon from '../../../assets/Admin/RequestImages/reject-icon.svg';
import { axiosInstance } from '../../../utils/tokenUtils';
import toast from 'react-hot-toast';
import {
  trimString,
  isValidEmail,
  isValidName,
  isValidPhoneNumber,
  sanitizePhoneDigits,
} from '../../../components/Validation/Validation';
import usePagination from '@mui/material/usePagination';
import StarIcon from '@mui/icons-material/Star';

// Helper to allow digits and a single leading +
const sanitizePhoneDigitsAllowPlus = value => {
  if (!value) return '';
  const cleaned = String(value).replace(/[^0-9+]/g, '');
  // Keep only one leading +
  const hasLeadingPlus = cleaned.startsWith('+');
  const withoutPluses = cleaned.replace(/\+/g, '');
  return (hasLeadingPlus ? '+' : '') + withoutPluses;
};

// Utility function to format text (for Previous/Next labels)
const formatText = text => {
  if (!text) return text;
  return String(text)
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const rbacCountryCodeMap = {
  AF: { code: '+93', name: 'Afghanistan' },
  AL: { code: '+355', name: 'Albania' },
  DZ: { code: '+213', name: 'Algeria' },
  AS: { code: '+1684', name: 'American Samoa' },
  AD: { code: '+376', name: 'Andorra' },
  AO: { code: '+244', name: 'Angola' },
  AI: { code: '+1264', name: 'Anguilla' },
  AG: { code: '+1268', name: 'Antigua and Barbuda' },
  AR: { code: '+54', name: 'Argentina' },
  AM: { code: '+374', name: 'Armenia' },
  AW: { code: '+297', name: 'Aruba' },
  AU: { code: '+61', name: 'Australia' },
  AT: { code: '+43', name: 'Austria' },
  AZ: { code: '+994', name: 'Azerbaijan' },
  BS: { code: '+1242', name: 'Bahamas' },
  BH: { code: '+973', name: 'Bahrain' },
  BD: { code: '+880', name: 'Bangladesh' },
  BB: { code: '+1246', name: 'Barbados' },
  BY: { code: '+375', name: 'Belarus' },
  BE: { code: '+32', name: 'Belgium' },
  BZ: { code: '+501', name: 'Belize' },
  BJ: { code: '+229', name: 'Benin' },
  BM: { code: '+1441', name: 'Bermuda' },
  BT: { code: '+975', name: 'Bhutan' },
  BO: { code: '+591', name: 'Bolivia' },
  BA: { code: '+387', name: 'Bosnia and Herzegovina' },
  BW: { code: '+267', name: 'Botswana' },
  BR: { code: '+55', name: 'Brazil' },
  BN: { code: '+673', name: 'Brunei' },
  BG: { code: '+359', name: 'Bulgaria' },
  BF: { code: '+226', name: 'Burkina Faso' },
  BI: { code: '+257', name: 'Burundi' },
  KH: { code: '+855', name: 'Cambodia' },
  CM: { code: '+237', name: 'Cameroon' },
  CA: { code: '+1', name: 'Canada' },
  CV: { code: '+238', name: 'Cape Verde' },
  KY: { code: '+1345', name: 'Cayman Islands' },
  CF: { code: '+236', name: 'Central African Republic' },
  TD: { code: '+235', name: 'Chad' },
  CL: { code: '+56', name: 'Chile' },
  CN: { code: '+86', name: 'China' },
  CO: { code: '+57', name: 'Colombia' },
  KM: { code: '+269', name: 'Comoros' },
  CG: { code: '+242', name: 'Congo' },
  CD: { code: '+243', name: 'Congo, Democratic Republic' },
  CK: { code: '+682', name: 'Cook Islands' },
  CR: { code: '+506', name: 'Costa Rica' },
  CI: { code: '+225', name: "Cote d'Ivoire" },
  HR: { code: '+385', name: 'Croatia' },
  CU: { code: '+53', name: 'Cuba' },
  CY: { code: '+357', name: 'Cyprus' },
  CZ: { code: '+420', name: 'Czech Republic' },
  DK: { code: '+45', name: 'Denmark' },
  DJ: { code: '+253', name: 'Djibouti' },
  DM: { code: '+1767', name: 'Dominica' },
  DO: { code: '+1', name: 'Dominican Republic' },
  EC: { code: '+593', name: 'Ecuador' },
  EG: { code: '+20', name: 'Egypt' },
  SV: { code: '+503', name: 'El Salvador' },
  GQ: { code: '+240', name: 'Equatorial Guinea' },
  ER: { code: '+291', name: 'Eritrea' },
  EE: { code: '+372', name: 'Estonia' },
  ET: { code: '+251', name: 'Ethiopia' },
  FK: { code: '+500', name: 'Falkland Islands' },
  FO: { code: '+298', name: 'Faroe Islands' },
  FJ: { code: '+679', name: 'Fiji' },
  FI: { code: '+358', name: 'Finland' },
  FR: { code: '+33', name: 'France' },
  GF: { code: '+594', name: 'French Guiana' },
  PF: { code: '+689', name: 'French Polynesia' },
  GA: { code: '+241', name: 'Gabon' },
  GM: { code: '+220', name: 'Gambia' },
  GE: { code: '+995', name: 'Georgia' },
  DE: { code: '+49', name: 'Germany' },
  GH: { code: '+233', name: 'Ghana' },
  GI: { code: '+350', name: 'Gibraltar' },
  GR: { code: '+30', name: 'Greece' },
  GL: { code: '+299', name: 'Greenland' },
  GD: { code: '+1473', name: 'Grenada' },
  GP: { code: '+590', name: 'Guadeloupe' },
  GU: { code: '+1671', name: 'Guam' },
  GT: { code: '+502', name: 'Guatemala' },
  GN: { code: '+224', name: 'Guinea' },
  GW: { code: '+245', name: 'Guinea-Bissau' },
  GY: { code: '+592', name: 'Guyana' },
  HT: { code: '+509', name: 'Haiti' },
  HN: { code: '+504', name: 'Honduras' },
  HK: { code: '+852', name: 'Hong Kong' },
  HU: { code: '+36', name: 'Hungary' },
  IS: { code: '+354', name: 'Iceland' },
  IN: { code: '+91', name: 'India' },
  ID: { code: '+62', name: 'Indonesia' },
  IR: { code: '+98', name: 'Iran' },
  IQ: { code: '+964', name: 'Iraq' },
  IE: { code: '+353', name: 'Ireland' },
  IL: { code: '+972', name: 'Israel' },
  IT: { code: '+39', name: 'Italy' },
  JM: { code: '+1876', name: 'Jamaica' },
  JP: { code: '+81', name: 'Japan' },
  JO: { code: '+962', name: 'Jordan' },
  KZ: { code: '+7', name: 'Kazakhstan' },
  KE: { code: '+254', name: 'Kenya' },
  KI: { code: '+686', name: 'Kiribati' },
  KW: { code: '+965', name: 'Kuwait' },
  KG: { code: '+996', name: 'Kyrgyzstan' },
  LA: { code: '+856', name: 'Laos' },
  LV: { code: '+371', name: 'Latvia' },
  LB: { code: '+961', name: 'Lebanon' },
  LS: { code: '+266', name: 'Lesotho' },
  LR: { code: '+231', name: 'Liberia' },
  LY: { code: '+218', name: 'Libya' },
  LI: { code: '+423', name: 'Liechtenstein' },
  LT: { code: '+370', name: 'Lithuania' },
  LU: { code: '+352', name: 'Luxembourg' },
  MO: { code: '+853', name: 'Macau' },
  MK: { code: '+389', name: 'Macedonia' },
  MG: { code: '+261', name: 'Madagascar' },
  MW: { code: '+265', name: 'Malawi' },
  MY: { code: '+60', name: 'Malaysia' },
  MV: { code: '+960', name: 'Maldives' },
  ML: { code: '+223', name: 'Mali' },
  MT: { code: '+356', name: 'Malta' },
  MH: { code: '+692', name: 'Marshall Islands' },
  MQ: { code: '+596', name: 'Martinique' },
  MR: { code: '+222', name: 'Mauritania' },
  MU: { code: '+230', name: 'Mauritius' },
  YT: { code: '+262', name: 'Mayotte' },
  MX: { code: '+52', name: 'Mexico' },
  FM: { code: '+691', name: 'Micronesia' },
  MD: { code: '+373', name: 'Moldova' },
  MC: { code: '+377', name: 'Monaco' },
  MN: { code: '+976', name: 'Mongolia' },
  ME: { code: '+382', name: 'Montenegro' },
  MS: { code: '+1664', name: 'Montserrat' },
  MA: { code: '+212', name: 'Morocco' },
  MZ: { code: '+258', name: 'Mozambique' },
  MM: { code: '+95', name: 'Myanmar' },
  NA: { code: '+264', name: 'Namibia' },
  NR: { code: '+674', name: 'Nauru' },
  NP: { code: '+977', name: 'Nepal' },
  NL: { code: '+31', name: 'Netherlands' },
  NC: { code: '+687', name: 'New Caledonia' },
  NZ: { code: '+64', name: 'New Zealand' },
  NI: { code: '+505', name: 'Nicaragua' },
  NE: { code: '+227', name: 'Niger' },
  NG: { code: '+234', name: 'Nigeria' },
  NU: { code: '+683', name: 'Niue' },
  NF: { code: '+672', name: 'Norfolk Island' },
  KP: { code: '+850', name: 'North Korea' },
  MP: { code: '+1670', name: 'Northern Mariana Islands' },
  NO: { code: '+47', name: 'Norway' },
  OM: { code: '+968', name: 'Oman' },
  PK: { code: '+92', name: 'Pakistan' },
  PW: { code: '+680', name: 'Palau' },
  PS: { code: '+970', name: 'Palestine' },
  PA: { code: '+507', name: 'Panama' },
  PG: { code: '+675', name: 'Papua New Guinea' },
  PY: { code: '+595', name: 'Paraguay' },
  PE: { code: '+51', name: 'Peru' },
  PH: { code: '+63', name: 'Philippines' },
  PL: { code: '+48', name: 'Poland' },
  PT: { code: '+351', name: 'Portugal' },
  PR: { code: '+1', name: 'Puerto Rico' },
  QA: { code: '+974', name: 'Qatar' },
  RE: { code: '+262', name: 'Reunion' },
  RO: { code: '+40', name: 'Romania' },
  RU: { code: '+7', name: 'Russia' },
  RW: { code: '+250', name: 'Rwanda' },
  KN: { code: '+1869', name: 'Saint Kitts and Nevis' },
  LC: { code: '+1758', name: 'Saint Lucia' },
  VC: { code: '+1784', name: 'Saint Vincent and the Grenadines' },
  WS: { code: '+685', name: 'Samoa' },
  SM: { code: '+378', name: 'San Marino' },
  ST: { code: '+239', name: 'Sao Tome and Principe' },
  SA: { code: '+966', name: 'Saudi Arabia' },
  SN: { code: '+221', name: 'Senegal' },
  RS: { code: '+381', name: 'Serbia' },
  SC: { code: '+248', name: 'Seychelles' },
  SL: { code: '+232', name: 'Sierra Leone' },
  SG: { code: '+65', name: 'Singapore' },
  SK: { code: '+421', name: 'Slovakia' },
  SI: { code: '+386', name: 'Slovenia' },
  SB: { code: '+677', name: 'Solomon Islands' },
  SO: { code: '+252', name: 'Somalia' },
  ZA: { code: '+27', name: 'South Africa' },
  KR: { code: '+82', name: 'South Korea' },
  SS: { code: '+211', name: 'South Sudan' },
  ES: { code: '+34', name: 'Spain' },
  LK: { code: '+94', name: 'Sri Lanka' },
  SD: { code: '+249', name: 'Sudan' },
  SR: { code: '+597', name: 'Suriname' },
  SZ: { code: '+268', name: 'Swaziland' },
  SE: { code: '+46', name: 'Sweden' },
  CH: { code: '+41', name: 'Switzerland' },
  SY: { code: '+963', name: 'Syria' },
  TW: { code: '+886', name: 'Taiwan' },
  TJ: { code: '+992', name: 'Tajikistan' },
  TZ: { code: '+255', name: 'Tanzania' },
  TH: { code: '+66', name: 'Thailand' },
  TL: { code: '+670', name: 'Timor-Leste' },
  TG: { code: '+228', name: 'Togo' },
  TK: { code: '+690', name: 'Tokelau' },
  TO: { code: '+676', name: 'Tonga' },
  TT: { code: '+1868', name: 'Trinidad and Tobago' },
  TN: { code: '+216', name: 'Tunisia' },
  TR: { code: '+90', name: 'Turkey' },
  TM: { code: '+993', name: 'Turkmenistan' },
  TC: { code: '+1649', name: 'Turks and Caicos Islands' },
  TV: { code: '+688', name: 'Tuvalu' },
  UG: { code: '+256', name: 'Uganda' },
  UA: { code: '+380', name: 'Ukraine' },
  AE: { code: '+971', name: 'United Arab Emirates' },
  GB: { code: '+44', name: 'United Kingdom' },
  US: { code: '+1', name: 'United States' },
  UY: { code: '+598', name: 'Uruguay' },
  UZ: { code: '+998', name: 'Uzbekistan' },
  VU: { code: '+678', name: 'Vanuatu' },
  VA: { code: '+39', name: 'Vatican City' },
  VE: { code: '+58', name: 'Venezuela' },
  VN: { code: '+84', name: 'Vietnam' },
  VG: { code: '+1284', name: 'Virgin Islands, British' },
  VI: { code: '+1340', name: 'Virgin Islands, U.S.' },
  WF: { code: '+681', name: 'Wallis and Futuna' },
  YE: { code: '+967', name: 'Yemen' },
  ZM: { code: '+260', name: 'Zambia' },
  ZW: { code: '+263', name: 'Zimbabwe' },
};

const rbacGetAllCountriesWithDialCodes = () => {
  return Object.entries(rbacCountryCodeMap)
    .map(([countryCode, data]) => ({
      countryCode,
      dialCode: data.code,
      name: data.name,
      displayText: `${data.code} (${data.name})`,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

const rbacParsePhoneNumber = fullPhoneNumber => {
  if (!fullPhoneNumber || !fullPhoneNumber.startsWith('+')) {
    return { countryCode: '+91', phoneNumber: fullPhoneNumber || '' };
  }
  const sortedCodes = Object.values(rbacCountryCodeMap)
    .map(c => c.code)
    .sort((a, b) => b.length - a.length);
  for (const code of sortedCodes) {
    if (fullPhoneNumber.startsWith(code)) {
      return { countryCode: code, phoneNumber: fullPhoneNumber.substring(code.length) };
    }
  }
  return { countryCode: '+91', phoneNumber: fullPhoneNumber.substring(1) };
};

const rbacCombinePhoneNumber = (countryCode, phoneNumber) => {
  if (!countryCode || !phoneNumber) return '';
  return `${countryCode}${phoneNumber}`;
};

const Rbac = () => {
  const [admins, setAdmins] = useState([]);
  const [rbacMe, setRbacMe] = useState(null);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [uiRoleFilter, setUiRoleFilter] = useState('all');
  const [openFilter, setOpenFilter] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const [selectedAdminForAction, setSelectedAdminForAction] = useState(null);
  const [stats, setStats] = useState({
    total_admins: 0,
    active_admins: 0,
    super_admins: 0,
    available_roles: 0,
  });

  const roles = [
    {
      value: 'operations_officer',
      label: 'Operations Officer',
      color: '#279BE2',
      bgColor: '#CCECFF',
      textColor: '#279BE2',
    },
    {
      value: 'talent_manager',
      label: 'Talent Manager',
      color: '#D84F4F',
      bgColor: '#FFE2E2',
      textColor: '#D84F4F',
    },
    {
      value: 'finance_officer',
      label: 'Finance Officer',
      color: '#BC8D00',
      bgColor: '#FFF3CE',
      textColor: '#BC8D00',
    },
    {
      value: 'compliance_auditor',
      label: 'Compliance Auditor',
      color: '#0DBC00',
      bgColor: '#DDFFCE',
      textColor: '#0DBC00',
    },
    {
      value: 'support_associate',
      label: 'Support Associate',
      color: '#00A8CC',
      bgColor: '#CCF0F8',
      textColor: '#00A8CC',
    },
    {
      value: 'super_admin',
      label: 'Super Admin',
      color: '#6B60E7',
      bgColor: '#F0EFFF',
      textColor: '#6B60E7',
    },
    { value: 'admin', label: 'Admin', color: '#B353CB', bgColor: '#EACEF1', textColor: '#B353CB' },
  ];

  const statusColors = {
    active: '#28a745',
    inactive: '#6c757d',
  };

  // Helper function to check if user has a specific permission
  const hasPermission = permission => {
    if (!rbacMe || !Array.isArray(rbacMe.permissions)) return false;
    return rbacMe.permissions.includes(permission);
  };

  // Check if current user can manage RBAC
  const canManageRBAC = () => {
    return hasPermission('manage_rbac') || (rbacMe?.roles || []).includes('super_admin');
  };

  const handleChangeSearch = e => {
    setSearchTerm(e.target.value);
  };

  const handleSearchKeyDown = e => {
    if (e.key === 'Enter') {
      setDebouncedSearch(searchTerm.trim());
      setPage(1);
    }
  };

  const handleClickToOpenFilter = event => {
    setUiRoleFilter(roleFilter);
    setFilterAnchorEl(event.currentTarget);
    setOpenFilter(true);
  };

  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
    setOpenFilter(false);
  };

  const handleApplyFilter = () => {
    setRoleFilter(uiRoleFilter);
    setPage(1);
    handleCloseFilter();
  };

  const handleClearAll = () => {
    setUiRoleFilter('all');
  };

  // debounce search input
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  // Load current user permissions
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        let cachedData = null;
        try {
          const cached = sessionStorage.getItem('rbac_admin_me');
          if (cached) {
            cachedData = JSON.parse(cached);
            setRbacMe(cachedData);
          }
        } catch (_) {}

        const { data } = await axiosInstance.get('/admin/rbac/admin/me');
        setRbacMe(data);

        try {
          sessionStorage.setItem('rbac_admin_me', JSON.stringify(data));
        } catch (_) {}
      } catch (_) {
        if (!rbacMe) setRbacMe({ roles: [], permissions: [] });
      }
    };

    fetchCurrentUser();
  }, []);

  const fetchStats = async () => {
    try {
      const { data } = await axiosInstance.get('/admin/rbac/stats');
      setStats({
        total_admins: data?.total_admins ?? 0,
        active_admins: data?.active_admins ?? 0,
        super_admins: data?.super_admins ?? 0,
        available_roles: data?.available_roles ?? 0,
      });
    } catch (error) {
      // optional: toast on stats error
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  // reusable fetch function
  const fetchAdmins = async (overrides = {}) => {
    try {
      setLoading(true);
      const effectivePage = overrides.page ?? page;
      const effectivePageSize = overrides.page_size ?? pageSize;
      const effectiveSearch = overrides.search_query ?? debouncedSearch;
      const effectiveRole = overrides.role ?? roleFilter;

      const params = { page: effectivePage, page_size: effectivePageSize };
      if (effectiveSearch) params.search_query = effectiveSearch;
      if (effectiveRole !== 'all') params.role = effectiveRole;

      const { data } = await axiosInstance.get('/admin/rbac/admins', { params });
      setTotalCount(data?.count ?? 0);
      const mapped = (data?.results || []).map(item => ({
        id: item.id,
        name: item.full_name,
        email: item.email,
        phone: item.phone_no,
        role: Array.isArray(item.roles) && item.roles.length > 0 ? item.roles[0] : 'admin',
        status: item.is_active ? 'active' : 'inactive',
        permissions: item.permissions || [],
        avatar: '/placeholder.svg',
      }));
      setAdmins(mapped);
    } catch (error) {
      toast.dismiss();
      toast.error(
        error?.response?.data?.detail || error?.response?.data?.message || 'Failed to load admins'
      );
    } finally {
      setLoading(false);
    }
  };

  // fetch on filters/pagination change
  useEffect(() => {
    fetchAdmins();
  }, [debouncedSearch, roleFilter, page, pageSize]);

  // Shared pagination UI handlers
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = event => {
    const next = parseInt(event.target.value, 10);
    setPageSize(next);
    setPage(1);
  };

  // Pagination hook
  const { items } = usePagination({
    count: Math.max(1, Math.ceil(totalCount / pageSize)),
    page: page,
    onChange: handlePageChange,
  });

  const sortedAdmins = [...admins].sort((a, b) => {
    // Super admins always come first
    if (a.role === 'super_admin' && b.role !== 'super_admin') return -1;
    if (a.role !== 'super_admin' && b.role === 'super_admin') return 1;

    // If both are super admins or both are not super admins, sort by name
    return a.name.localeCompare(b.name);
  });

  const getRoleLabel = roleValue => {
    const role = roles.find(r => r.value === roleValue);
    return role ? role.label : roleValue;
  };

  const getRoleColor = roleValue => {
    const role = roles.find(r => r.value === roleValue);
    return role ? role.color : '#6c757d';
  };

  const getRoleBgColor = roleValue => {
    const role = roles.find(r => r.value === roleValue);
    return role ? role.bgColor : '#6c757d';
  };

  const getRoleTextColor = roleValue => {
    const role = roles.find(r => r.value === roleValue);
    return role ? role.textColor : 'white';
  };

  const handleAddAdmin = () => {
    if (!canManageRBAC()) {
      toast.dismiss();
      toast.error("You don't have permission to add admins");
      return;
    }
    setEditingAdmin(null);
    setOpenDialog(true);
  };

  const handleActionMenuClick = (event, admin) => {
    event.stopPropagation();
    setActionMenuAnchorEl(event.currentTarget);
    setSelectedAdminForAction(admin);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchorEl(null);
    setSelectedAdminForAction(null);
  };

  const handleEditAdmin = admin => {
    if (!canManageRBAC()) {
      toast.dismiss();
      toast.error("You don't have permission to edit admins");
      handleActionMenuClose();
      return;
    }
    if (admin.role === 'super_admin') {
      toast.dismiss();
      toast.error('Super Admin cannot be edited');
      handleActionMenuClose();
      return;
    }
    setEditingAdmin(admin);
    setOpenDialog(true);
    handleActionMenuClose();
  };

  const handleDeleteAdmin = adminId => {
    if (!adminId) return;
    if (!canManageRBAC()) {
      toast.dismiss();
      toast.error("You don't have permission to delete admins");
      handleActionMenuClose();
      return;
    }
    const target = admins.find(a => a.id === adminId);
    if (!target) {
      handleActionMenuClose();
      return;
    }
    if (target?.role === 'super_admin') {
      toast.dismiss();
      toast.error('Super Admin cannot be deleted');
      handleActionMenuClose();
      return;
    }
    setAdminToDelete(target);
    setDeleteConfirmOpen(true);
    handleActionMenuClose();
  };

  const confirmDeleteAdmin = async () => {
    if (!adminToDelete) return;
    try {
      await axiosInstance.delete(`/admin/rbac/admins/${adminToDelete.id}`);
      toast.dismiss();
      toast.success('Admin deleted successfully');
      setDeleteConfirmOpen(false);
      setAdminToDelete(null);
      await fetchAdmins();
      fetchStats();
    } catch (error) {
      toast.dismiss();
      toast.error(
        error?.response?.data?.detail || error?.response?.data?.message || 'Failed to delete admin'
      );
      setDeleteConfirmOpen(false);
      setAdminToDelete(null);
    }
  };

  const handleSaveAdmin = async adminData => {
    if (!canManageRBAC()) {
      toast.dismiss();
      toast.error("You don't have permission to modify admins");
      return;
    }
    try {
      if (adminData.role === 'super_admin') {
        toast.dismiss();
        toast.error('Super Admin cannot be created or assigned');
        return;
      }

      // Prepare permissions for organization managers
      let user_specific_permissions = [];
      if (adminData.role === 'operations_officer') {
        // Only include permissions if checkbox is checked, otherwise send empty array
        // to explicitly remove permissions
        if (adminData.bankPayoutAccess) {
          user_specific_permissions = ['manage_bank_details', 'manage_payouts'];
        }
      }

      // Prepare permissions for finance managers
      if (adminData.role === 'finance_officer') {
        if (adminData.feeSettingsAccess) {
          user_specific_permissions = [
            'manage_fee_settings',
            'manage_individual_payout',
            'manage_bank_details',
            'manage_organisations',
          ];
        }
      }

      // Prepare permissions for compliance auditor
      if (adminData.role === 'compliance_auditor') {
        console.log(
          '[RBAC] Compliance Auditor - downloadPayoutAccess:',
          adminData.downloadPayoutAccess
        );
        if (adminData.downloadPayoutAccess) {
          user_specific_permissions = ['download_bank_transaction_report'];
          console.log(
            '[RBAC] Setting user_specific_permissions for compliance_auditor:',
            user_specific_permissions
          );
        } else {
          console.log('[RBAC] downloadPayoutAccess is false, sending empty permissions array');
          user_specific_permissions = [];
        }
      }

      const cleaned = {
        name: trimString(adminData.name),
        email: trimString(adminData.email),
        phone: rbacCombinePhoneNumber(
          adminData.phone_country_code || '+91',
          sanitizePhoneDigits(adminData.phone_number_only)
        ),
        role: adminData.role,
        password: trimString(adminData.password || ''),
        status: adminData.status,
      };

      if (editingAdmin) {
        if (editingAdmin.role === 'super_admin') {
          toast.dismiss();
          toast.error('Super Admins cannot be edited');
          return;
        }
        const payload = {
          full_name: cleaned.name,
          email: cleaned.email,
          phone_no: cleaned.phone,
          role: cleaned.role,
          is_active: cleaned.status === 'active',
        };

        // Always include user_specific_permissions when the role is operations_officer
        // This allows for either adding or removing these permissions
        if (cleaned.role === 'operations_officer') {
          payload.user_specific_permissions = user_specific_permissions;
        }

        // Always include user_specific_permissions when the role is finance_manager
        // This allows for either adding or removing these permissions
        if (cleaned.role === 'finance_officer') {
          payload.user_specific_permissions = user_specific_permissions;
        }

        // Always include user_specific_permissions when the role is compliance_auditor
        // This allows for either adding or removing these permissions
        if (cleaned.role === 'compliance_auditor') {
          console.log(
            '[RBAC] Updating compliance_auditor - user_specific_permissions:',
            user_specific_permissions
          );
          payload.user_specific_permissions = user_specific_permissions;
        }

        console.log('[RBAC] Final payload being sent (PATCH):', payload);
        await axiosInstance.patch(`/admin/rbac/admins/${editingAdmin.id}`, payload);
        await fetchAdmins();
        fetchStats();
        toast.dismiss();
        toast.success('Admin updated successfully');
      } else {
        const payload = {
          full_name: cleaned.name,
          email: cleaned.email,
          phone_no: cleaned.phone,
          role: cleaned.role,
          password: cleaned.password,
        };

        if (cleaned.role === 'operations_officer') {
          payload.user_specific_permissions = user_specific_permissions;
        }

        if (cleaned.role === 'finance_officer') {
          payload.user_specific_permissions = user_specific_permissions;
        }

        if (cleaned.role === 'compliance_auditor') {
          console.log(
            '[RBAC] Creating new compliance_auditor - user_specific_permissions:',
            user_specific_permissions
          );
          payload.user_specific_permissions = user_specific_permissions;
        }

        console.log('[RBAC] Final payload being sent:', payload);
        await axiosInstance.post('/admin/rbac/admins', payload);
        // Refresh list from server and reset to first page to include new record
        await fetchAdmins({ page: 1 });
        setPage(1);
        fetchStats();
        toast.dismiss();
        toast.success('Admin added successfully');
      }
      setOpenDialog(false);
    } catch (error) {
      toast.dismiss();
      const status = error?.response?.status;
      const data = error?.response?.data;
      let message = 'Operation failed';

      if (status === 422 && Array.isArray(data?.detail)) {
        const first = data.detail[0];
        const field = Array.isArray(first?.loc) ? first.loc[first.loc.length - 1] : undefined;
        if (field === 'role') {
          message = 'Selected role is not valid. Please choose one of the available roles.';
        } else if (typeof first?.msg === 'string') {
          message = first.msg;
        }
      } else if (typeof data?.detail === 'string') {
        message = data.detail;
      } else if (typeof data?.message === 'string') {
        message = data.message;
      }

      toast.error(message);
    }
  };

  return (
    <Layout title="Klass Online | RBAC">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>
        <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
          <Box className="flex flex-col p-[1rem] gap-4">
            {/* Header */}
            <Box className="flex justify-between items-center">
              <Typography className="request-title">Role-Based Access Control</Typography>
              <Box className="flex items-center gap-4 w-[50%]">
                <Input
                  sx={{
                    textDecoration: 'none',
                    border: '1px solid var(--Stroke, #D5DEE3)',
                    borderRadius: '56px',
                    '&:before, &:after': {
                      borderBottom: 'none !important',
                    },
                    '&:hover:not(.Mui-disabled):before': {
                      borderBottom: 'none !important',
                    },
                    padding: '8px 10px',
                    '& .MuiInputBase-input': {
                      padding: '3px 5px 3px 5px',
                    },
                    flex: 1,
                    minWidth: '200px',
                    height: '40px',
                  }}
                  type="search"
                  placeholder="Search admins..."
                  aria-label="Search"
                  value={searchTerm}
                  onChange={handleChangeSearch}
                  onKeyDown={handleSearchKeyDown}
                  startAdornment={<SearchIcon className="search-icon-css" />}
                />
                {openFilter ? (
                  <img
                    src={ColoredFilterIcon || '/placeholder.svg'}
                    alt="filter"
                    onClick={handleCloseFilter}
                    style={{ width: 25, height: 25, cursor: 'pointer', flexShrink: 0 }}
                  />
                ) : (
                  <img
                    src={FilterIcon || '/placeholder.svg'}
                    alt="filter"
                    onClick={handleClickToOpenFilter}
                    style={{ width: 25, height: 25, cursor: 'pointer', flexShrink: 0 }}
                  />
                )}
                <Button
                  variant="contained"
                  startIcon={<AddIcon sx={{ fontSize: '12px' }} />}
                  onClick={handleAddAdmin}
                  className="rbac-add-button"
                  disabled={loading || !canManageRBAC()}
                  sx={{ flexShrink: 0 }}
                >
                  Add Admin
                </Button>
              </Box>
            </Box>

            <Popover
              open={openFilter}
              anchorEl={filterAnchorEl}
              onClose={handleCloseFilter}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ className: 'rbac-filter-modal-container' }}
            >
              <Box className="rbac-filter-modal-header">
                <Typography className="rbac-filter-modal-title">Filters</Typography>
                <IconButton
                  onClick={handleCloseFilter}
                  sx={{
                    padding: '6px',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <img src={closeIcon} alt="close" style={{ width: 18, height: 18 }} />
                </IconButton>
              </Box>
              <Box className="rbac-filter-modal-content">
                <Box className="rbac-filter-grid">
                  <Box className="rbac-filter-item">
                    <label className="rbac-filter-label">Role</label>
                    <FormControl sx={{ width: '100%' }}>
                      <Select
                        value={uiRoleFilter}
                        onChange={e => setUiRoleFilter(e.target.value)}
                        displayEmpty
                        className="rbac-filter-input"
                        IconComponent={KeyboardArrowDownIcon}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        }}
                      >
                        <MenuItem value="all" className="rbac-dropdown-option">
                          All Roles
                        </MenuItem>
                        {roles.map(role => (
                          <MenuItem
                            key={role.value}
                            value={role.value}
                            className="rbac-dropdown-option"
                          >
                            {role.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </Box>
              <Box className="rbac-filter-buttons-container">
                <Button
                  variant="outlined"
                  onClick={handleClearAll}
                  className="rbac-filter-clear-button"
                >
                  Clear All
                </Button>
                <Button
                  variant="contained"
                  onClick={handleApplyFilter}
                  className="rbac-filter-apply-button"
                >
                  Apply
                </Button>
              </Box>
            </Popover>

            {/* Stats Cards */}
            <Box className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Box className="rbac-stat-card">
                <Typography className="rbac-stat-label">Total Admins</Typography>
                <Typography className="rbac-stat-number">{stats.total_admins}</Typography>
              </Box>
              <Box className="rbac-stat-card">
                <Typography className="rbac-stat-label">Active Admins</Typography>
                <Typography className="rbac-stat-number">{stats.active_admins}</Typography>
              </Box>
              <Box className="rbac-stat-card">
                <Typography className="rbac-stat-label">Super Admins</Typography>
                <Typography className="rbac-stat-number">{stats.super_admins}</Typography>
              </Box>
              <Box className="rbac-stat-card">
                <Typography className="rbac-stat-label">Available Roles</Typography>
                <Typography className="rbac-stat-number">{stats.available_roles}</Typography>
              </Box>
            </Box>

            {/* Admins Table */}
            <Box className="bg-white rounded-lg overflow-hidden">
              <TableContainer
                component={Paper}
                className="rbac-table-container"
                sx={{ borderRadius: '8px', boxShadow: 'none', background: 'white' }}
                elevation={0}
              >
                <Table className="rbac-table">
                  <TableHead className="rbac-table-head">
                    <TableRow className="rbac-table-header">
                      <TableCell className="rbac-table-header-cell">Admin</TableCell>
                      <TableCell className="rbac-table-header-cell">Contact</TableCell>
                      <TableCell className="rbac-table-header-cell">Role</TableCell>
                      <TableCell className="rbac-table-header-cell">Status</TableCell>
                      <TableCell className="rbac-table-header-cell">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody className={!loading ? 'rbac-fade-in' : ''}>
                    {loading &&
                      [...Array(8)].map((_, index) => (
                        <TableRow key={index} className="rbac-table-row">
                          <TableCell className="rbac-table-cell">
                            <Box className="rbac-name-container">
                              <div
                                className="skeleton-text"
                                style={{ width: 25, height: 25, borderRadius: '50%' }}
                              />
                              <div className="skeleton-text" style={{ width: '120px' }} />
                            </Box>
                          </TableCell>
                          <TableCell className="rbac-table-cell">
                            <Box>
                              <div
                                className="skeleton-text"
                                style={{ width: '160px', marginBottom: 4 }}
                              />
                              <div className="skeleton-text" style={{ width: '100px' }} />
                            </Box>
                          </TableCell>
                          <TableCell className="rbac-table-cell">
                            <div className="skeleton-text" style={{ width: '100px' }} />
                          </TableCell>
                          <TableCell className="rbac-table-cell">
                            <div className="skeleton-text" style={{ width: '80px' }} />
                          </TableCell>
                          <TableCell className="rbac-table-cell">
                            <Box className="flex gap-2">
                              <div
                                className="skeleton-text"
                                style={{ width: 32, height: 32, borderRadius: 6 }}
                              />
                              <div
                                className="skeleton-text"
                                style={{ width: 32, height: 32, borderRadius: 6 }}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}

                    {!loading && sortedAdmins.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          sx={{
                            textAlign: 'center',
                            padding: '32px 16px',
                            color: '#6b7280',
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Poppins',
                              fontSize: '14px',
                              textAlign: 'center',
                            }}
                          >
                            No user found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}

                    {!loading &&
                      sortedAdmins.length > 0 &&
                      sortedAdmins.map(admin => (
                        <TableRow key={admin.id} className="rbac-table-row">
                          <TableCell className="rbac-table-cell">
                            <Box className="rbac-name-container">
                              <Avatar src={admin.avatar} sx={{ width: 25, height: 25 }} />
                              <Typography className="rbac-admin-name">{admin.name}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell className="rbac-table-cell">
                            <Box>
                              <Typography className="rbac-contact-email">{admin.email}</Typography>
                              <Typography className="rbac-contact-phone">{admin.phone}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell className="rbac-table-cell">
                            <Chip
                              icon={
                                ((admin.role === 'operations_officer' ||
                                  admin.role === 'organisation_manager') &&
                                  Array.isArray(admin.permissions) &&
                                  admin.permissions.includes('manage_bank_details') &&
                                  admin.permissions.includes('manage_payouts')) ||
                                (admin.role === 'finance_officer' &&
                                  Array.isArray(admin.permissions) &&
                                  admin.permissions.includes('manage_fee_settings')) ||
                                (admin.role === 'compliance_auditor' &&
                                  Array.isArray(admin.permissions) &&
                                  admin.permissions.includes(
                                    'download_bank_transaction_report'
                                  )) ? (
                                  <StarIcon className="rbac-role-star-icon" />
                                ) : undefined
                              }
                              label={getRoleLabel(admin.role)}
                              className={`rbac-role-chip ${admin.role === 'super_admin' ? 'rbac-role-chip-super-admin' : ''} ${admin.role === 'finance_officer' ? 'rbac-role-chip-finance-officer' : ''} ${admin.role === 'compliance_auditor' ? 'rbac-role-chip-compliance-auditor' : ''} ${admin.role === 'support_associate' ? 'rbac-role-chip-support-associate' : ''}`}
                              style={{
                                backgroundColor: getRoleBgColor(admin.role),
                                color: getRoleTextColor(admin.role),
                              }}
                            />
                          </TableCell>
                          <TableCell className="rbac-table-cell">
                            <Chip
                              label={admin.status.charAt(0).toUpperCase() + admin.status.slice(1)}
                              className={`rbac-status-chip ${admin.status}`}
                              style={
                                admin.status === 'active'
                                  ? {
                                      backgroundColor: '#F2FAF3',
                                      border: '1px solid #14B334',
                                      color: '#14B334',
                                    }
                                  : {
                                      backgroundColor: statusColors[admin.status],
                                      color: 'white',
                                    }
                              }
                            />
                          </TableCell>
                          <TableCell className="rbac-table-cell">
                            <IconButton
                              onClick={e => handleActionMenuClick(e, admin)}
                              className="rbac-action-button menu"
                              size="small"
                              disabled={!canManageRBAC()}
                            >
                              <MoreHorizOutlinedIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            {/* Shared Pagination UI */}
            {!loading && (
              <Box className="rbac-pagination-container">
                <Box className="rbac-pagination-info">
                  <Typography className="rbac-pagination-text">Showing</Typography>
                  <Select
                    value={pageSize}
                    onChange={handleRowsPerPageChange}
                    className="rbac-pagination-select"
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={15}>15</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={30}>30</MenuItem>
                  </Select>
                  <Typography className="rbac-pagination-text">of {totalCount} admins</Typography>
                </Box>
                <nav className="rbac-pagination-nav">
                  <ul className="rbac-pagination-list">
                    {items.map(({ page: itemPage, type, selected, ...item }, index) => {
                      let children = null;

                      if (type === 'start-ellipsis' || type === 'end-ellipsis') {
                        children = <span className="rbac-pagination-ellipsis">…</span>;
                      } else if (type === 'page') {
                        children = (
                          <button
                            type="button"
                            className={`rbac-pagination-button ${selected ? 'selected' : ''}`}
                            {...item}
                          >
                            {itemPage}
                          </button>
                        );
                      } else {
                        children = (
                          <button type="button" className="rbac-pagination-button" {...item}>
                            {formatText(type)}
                          </button>
                        );
                      }

                      return (
                        <li key={index} className="rbac-pagination-item">
                          {children}
                        </li>
                      );
                    })}
                  </ul>
                </nav>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Menu
        anchorEl={actionMenuAnchorEl}
        open={Boolean(actionMenuAnchorEl)}
        onClose={handleActionMenuClose}
        PaperProps={{
          sx: {
            minWidth: '120px',
            width: 'auto',
            padding: '4px 0',
            boxShadow: '0px 6px 10px rgba(10, 12, 18, 0.09)',
            border: '1px solid #D5DEE3',
            borderRadius: '8px',
          },
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            if (selectedAdminForAction) {
              handleEditAdmin(selectedAdminForAction);
            }
          }}
          sx={{
            padding: '8px 12px',
            minHeight: 'auto',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img src={EditIcon} alt="Edit" style={{ width: 16, height: 16 }} />
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '20px',
                color: '#14171F',
                whiteSpace: 'nowrap',
              }}
            >
              Edit
            </Typography>
          </Box>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedAdminForAction) {
              handleDeleteAdmin(selectedAdminForAction?.id);
            }
          }}
          sx={{
            padding: '8px 12px',
            minHeight: 'auto',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img src={DeleteIcon} alt="Delete" style={{ width: 16, height: 16 }} />
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '20px',
                color: '#E14949',
                whiteSpace: 'nowrap',
              }}
            >
              Delete
            </Typography>
          </Box>
        </MenuItem>
      </Menu>

      {/* Add/Edit Admin Dialog */}
      <AdminDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onSave={handleSaveAdmin}
        admin={editingAdmin}
        roles={roles}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirmOpen && adminToDelete && (
        <DeleteConfirmModal
          open={deleteConfirmOpen}
          onClose={() => {
            setDeleteConfirmOpen(false);
            setAdminToDelete(null);
          }}
          onConfirm={confirmDeleteAdmin}
          adminName={adminToDelete.name}
        />
      )}
    </Layout>
  );
};

// Admin Dialog Component
const AdminDialog = ({ open, onClose, onSave, admin, roles, showPassword, setShowPassword }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone_country_code: '+91',
    phone_number_only: '',
    role: '',
    password: '',
    status: 'active',
    bankPayoutAccess: false,
    feeSettingsAccess: false,
    downloadPayoutAccess: false,
  });

  // Track original admin data for change detection
  const [originalAdminData, setOriginalAdminData] = useState(null);

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    phone: false,
    role: false,
    password: false,
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  // RBAC country code dropdown state
  const [countryCodeDropdownOpen, setCountryCodeDropdownOpen] = useState(false);
  const [countryCodeSearch, setCountryCodeSearch] = useState('');
  const countryCodeDropdownRef = useRef(null);
  const countryCodeSearchRef = useRef(null);

  const rbacGetFilteredCountries = () => {
    const all = rbacGetAllCountriesWithDialCodes();
    if (!countryCodeSearch.trim()) return all;
    const term = countryCodeSearch.toLowerCase().trim();
    return all.filter(
      c =>
        c.name.toLowerCase().includes(term) ||
        c.dialCode.includes(term) ||
        c.countryCode.toLowerCase().includes(term)
    );
  };

  const rbacHandleCountryCodeSelect = dialCode => {
    setFormData(prev => ({ ...prev, phone_country_code: dialCode }));
    setCountryCodeDropdownOpen(false);
    setCountryCodeSearch('');
    // Mark as touched and revalidate using combined number
    setTouched(prev => ({ ...prev, phone: true }));
    const combined = rbacCombinePhoneNumber(
      dialCode,
      sanitizePhoneDigitsAllowPlus(formData.phone_number_only)
    );
    const message = validateField('phone', combined);
    setErrors(prev => ({ ...prev, phone: message }));
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        countryCodeDropdownRef.current &&
        !countryCodeDropdownRef.current.contains(event.target)
      ) {
        setCountryCodeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Define validator before it's used in effects/handlers
  const validateField = (field, value) => {
    switch (field) {
      case 'name': {
        const v = trimString(value);
        if (!v) return 'Full Name is required';
        if (!isValidName(v)) return 'Name must contain only letters';
        return '';
      }
      case 'email': {
        const v = trimString(value);
        if (!v) return 'Email is required';
        if (!isValidEmail(v)) return 'Invalid email format';
        return '';
      }
      case 'phone': {
        // value is expected to be the FULL phone with country code, may include +
        const full = sanitizePhoneDigitsAllowPlus(value);
        const digitsOnly = full.replace(/\D/g, '');
        if (!digitsOnly) return 'Phone number is required';
        // Count length including country code (excluding +)
        if (digitsOnly.length < 5 || digitsOnly.length > 15)
          return 'Phone must be 5-15 digits (including country code)';
        return '';
      }
      case 'role': {
        const v = trimString(value);
        if (!v) return 'Role is required';
        return '';
      }
      case 'password': {
        if (admin) return '';
        const v = trimString(value);
        if (!v) return 'Password is required';
        if (v.length < 6) return 'Password must be at least 6 characters';
        return '';
      }
      default:
        return '';
    }
  };

  // Check for organization manager permissions when editing an existing admin
  useEffect(() => {
    if (admin) {
      let hasBankPayoutPermissions = false;
      let hasFeeSettingsPermissions = false;
      let hasDownloadPayoutPermissions = false;
      if (
        (admin.role === 'operations_officer' || admin.role === 'organisation_manager') &&
        Array.isArray(admin.permissions)
      ) {
        hasBankPayoutPermissions =
          admin.permissions.includes('manage_bank_details') &&
          admin.permissions.includes('manage_payouts');
      }
      if (admin.role === 'finance_officer' && Array.isArray(admin.permissions)) {
        hasFeeSettingsPermissions = admin.permissions.includes('manage_fee_settings');
      }
      if (admin.role === 'compliance_auditor' && Array.isArray(admin.permissions)) {
        console.log('[RBAC] Loading Compliance Auditor - admin.permissions:', admin.permissions);
        hasDownloadPayoutPermissions = admin.permissions.includes(
          'download_bank_transaction_report'
        );
        console.log(
          '[RBAC] Compliance Auditor - hasDownloadPayoutPermissions:',
          hasDownloadPayoutPermissions
        );
      }
      const parsed = rbacParsePhoneNumber(admin.phone);
      const initialFormData = {
        name: admin.name,
        email: admin.email,
        phone_country_code: parsed.countryCode,
        phone_number_only: parsed.phoneNumber,
        role: admin.role,
        password: '',
        status: admin.status || 'active',
        bankPayoutAccess: hasBankPayoutPermissions,
        feeSettingsAccess: hasFeeSettingsPermissions,
        downloadPayoutAccess: hasDownloadPayoutPermissions,
      };
      console.log('[RBAC] Setting initial form data for admin:', {
        role: admin.role,
        permissions: admin.permissions,
        hasDownloadPayoutPermissions,
        initialFormData,
      });
      setFormData(initialFormData);

      // Store original data for change detection
      setOriginalAdminData({
        name: admin.name,
        email: admin.email,
        phone_country_code: parsed.countryCode,
        phone_number_only: parsed.phoneNumber,
        role: admin.role,
        status: admin.status || 'active',
        bankPayoutAccess: hasBankPayoutPermissions,
        feeSettingsAccess: hasFeeSettingsPermissions,
        downloadPayoutAccess: hasDownloadPayoutPermissions,
        downloadPayoutAccess: hasDownloadPayoutPermissions,
      });

      // If permissions were not provided in the list view, fetch the admin detail to ensure accuracy
      (async () => {
        try {
          if (
            (admin.role === 'operations_officer' ||
              admin.role === 'organisation_manager' ||
              admin.role === 'finance_officer' ||
              admin.role === 'compliance_auditor') &&
            (!Array.isArray(admin.permissions) || admin.permissions.length === 0) &&
            admin.id
          ) {
            console.log('[RBAC] Fetching admin details for role:', admin.role);
            const { data } = await axiosInstance.get(`/admin/rbac/admins/${admin.id}`);
            const perms = Array.isArray(data?.permissions) ? data.permissions : [];
            let updatedBankPayoutAccess = hasBankPayoutPermissions;
            let updatedFeeSettingsAccess = hasFeeSettingsPermissions;
            let updatedDownloadPayoutAccess = hasDownloadPayoutPermissions;

            if (admin.role === 'operations_officer' || admin.role === 'organisation_manager') {
              updatedBankPayoutAccess =
                perms.includes('manage_bank_details') && perms.includes('manage_payouts');
              setFormData(prev => ({ ...prev, bankPayoutAccess: updatedBankPayoutAccess }));
            }
            if (admin.role === 'finance_officer') {
              updatedFeeSettingsAccess = perms.includes('manage_fee_settings');
              setFormData(prev => ({ ...prev, feeSettingsAccess: updatedFeeSettingsAccess }));
            }
            if (admin.role === 'compliance_auditor') {
              console.log('[RBAC] Fetching Compliance Auditor permissions - perms:', perms);
              updatedDownloadPayoutAccess = perms.includes('download_bank_transaction_report');
              console.log(
                '[RBAC] Compliance Auditor - updatedDownloadPayoutAccess:',
                updatedDownloadPayoutAccess
              );
              setFormData(prev => ({ ...prev, downloadPayoutAccess: updatedDownloadPayoutAccess }));
            }

            // Update original data with fetched permissions
            setOriginalAdminData(prev => ({
              ...prev,
              bankPayoutAccess: updatedBankPayoutAccess,
              feeSettingsAccess: updatedFeeSettingsAccess,
              downloadPayoutAccess: updatedDownloadPayoutAccess,
            }));
          }
        } catch (_) {}
      })();

      // Mark all fields as touched when editing existing admin
      setTouched({
        name: true,
        email: true,
        phone: true,
        role: true,
        password: true,
      });
    } else {
      setFormData({
        name: '',
        email: '',
        phone_country_code: '+91',
        phone_number_only: '',
        role: '',
        password: '',
        status: 'active',
        bankPayoutAccess: false,
        feeSettingsAccess: false,
        downloadPayoutAccess: false,
      });

      // Reset touched state for new admin
      setTouched({
        name: false,
        email: false,
        phone: false,
        role: false,
        password: false,
      });

      // Clear original data for new admin
      setOriginalAdminData(null);
    }

    // Reset form submitted state when dialog opens/closes
    setFormSubmitted(false);

    // Compute validation errors but don't show them yet
    const initial = {};
    initial.name = validateField('name', admin ? admin.name : '');
    initial.email = validateField('email', admin ? admin.email : '');
    initial.phone = validateField('phone', admin ? admin.phone : rbacCombinePhoneNumber('+91', ''));
    initial.role = validateField('role', admin ? admin.role : '');
    initial.password = validateField('password', '');
    setErrors(initial);
  }, [admin, open]);

  const handleChange = (field, value) => {
    const nextValue = field === 'phone_number_only' ? sanitizePhoneDigitsAllowPlus(value) : value;
    setFormData(prev => ({ ...prev, [field]: nextValue }));

    // Mark field as touched
    setTouched(prev => ({ ...prev, [field === 'phone_number_only' ? 'phone' : field]: true }));

    // Reset bankPayoutAccess when changing from operations_officer to another role
    if (field === 'role' && value !== 'operations_officer' && value !== 'organisation_manager') {
      setFormData(prev => ({ ...prev, [field]: nextValue, bankPayoutAccess: false }));
    }

    // Reset feeSettingsAccess when changing from finance_officer to another role
    if (field === 'role' && value !== 'finance_officer') {
      setFormData(prev => ({ ...prev, [field]: nextValue, feeSettingsAccess: false }));
    }

    // Reset downloadPayoutAccess when changing from compliance_auditor to another role
    if (field === 'role' && value !== 'compliance_auditor') {
      setFormData(prev => ({ ...prev, [field]: nextValue, downloadPayoutAccess: false }));
    }

    const validationField = field === 'phone_number_only' ? 'phone' : field;
    const toValidate =
      validationField === 'phone'
        ? rbacCombinePhoneNumber(
            formData.phone_country_code,
            sanitizePhoneDigitsAllowPlus(nextValue)
          )
        : nextValue;
    const message = validateField(validationField, toValidate);
    setErrors(prev => ({ ...prev, [validationField]: message }));
  };

  // Check if form data has changed from original (for edit mode)
  const hasFormDataChanged = () => {
    if (!admin || !originalAdminData) return false; // New admin, no changes to detect

    const currentPhone = rbacCombinePhoneNumber(
      formData.phone_country_code,
      sanitizePhoneDigitsAllowPlus(formData.phone_number_only)
    );
    const originalPhone = rbacCombinePhoneNumber(
      originalAdminData.phone_country_code,
      sanitizePhoneDigitsAllowPlus(originalAdminData.phone_number_only)
    );

    return (
      trimString(formData.name) !== trimString(originalAdminData.name) ||
      trimString(formData.email) !== trimString(originalAdminData.email) ||
      currentPhone !== originalPhone ||
      formData.role !== originalAdminData.role ||
      formData.status !== originalAdminData.status ||
      formData.bankPayoutAccess !== originalAdminData.bankPayoutAccess ||
      formData.feeSettingsAccess !== originalAdminData.feeSettingsAccess ||
      formData.downloadPayoutAccess !== originalAdminData.downloadPayoutAccess
    );
  };

  const isFormValid = () => {
    const name = trimString(formData.name);
    const email = trimString(formData.email);
    const combinedPhone = rbacCombinePhoneNumber(
      formData.phone_country_code,
      sanitizePhoneDigitsAllowPlus(formData.phone_number_only)
    );
    const role = trimString(formData.role);
    const password = trimString(formData.password);

    const validations = {
      name: !validateField('name', name),
      email: !validateField('email', email),
      phone: !validateField('phone', combinedPhone),
      role: !validateField('role', role),
      password: admin ? true : !validateField('password', password),
    };

    return Object.values(validations).every(Boolean);
  };

  const validateForm = () => {
    // Mark all fields as touched when submitting
    setTouched({
      name: true,
      email: true,
      phone: true,
      role: true,
      password: true,
    });
    setFormSubmitted(true);

    const newErrors = {};

    const name = trimString(formData.name);
    const email = trimString(formData.email);
    const combinedPhone = rbacCombinePhoneNumber(
      formData.phone_country_code,
      sanitizePhoneDigitsAllowPlus(formData.phone_number_only)
    );
    const role = trimString(formData.role);
    const password = trimString(formData.password);

    const nameErr = validateField('name', name);
    if (nameErr) newErrors.name = nameErr;

    const emailErr = validateField('email', email);
    if (emailErr) newErrors.email = emailErr;

    const phoneErr = validateField('phone', combinedPhone);
    if (phoneErr) newErrors.phone = phoneErr;

    const roleErr = validateField('role', role);
    if (roleErr) newErrors.role = roleErr;

    const pwdErr = validateField('password', password);
    if (pwdErr) newErrors.password = pwdErr;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (validateForm()) {
      const cleaned = {
        name: trimString(formData.name),
        email: trimString(formData.email),
        phone_country_code: formData.phone_country_code,
        phone_number_only: sanitizePhoneDigitsAllowPlus(formData.phone_number_only),
        role: formData.role,
        password: trimString(formData.password),
        status: formData.status,
        bankPayoutAccess: formData.bankPayoutAccess,
        feeSettingsAccess: formData.feeSettingsAccess,
        downloadPayoutAccess: formData.downloadPayoutAccess,
      };
      console.log('[RBAC] handleSave - cleaned data:', cleaned);
      console.log('[RBAC] handleSave - formData:', formData);
      await onSave(cleaned);
    }
  };

  // Should only show error for touched fields or all fields if form submitted
  const shouldShowError = field => {
    return (touched[field] || formSubmitted) && errors[field];
  };

  const handleCountryCodeDropdownToggle = () => {
    const newState = !countryCodeDropdownOpen;
    setCountryCodeDropdownOpen(newState);
    if (newState) {
      setTimeout(() => {
        if (countryCodeSearchRef.current) {
          countryCodeSearchRef.current.focus();
        }
      }, 100);
    } else {
      setCountryCodeSearch('');
    }
  };

  const handleSearchKeyDown = e => {
    if (e.key === 'Escape') {
      setCountryCodeDropdownOpen(false);
      setCountryCodeSearch('');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const filteredCountries = rbacGetFilteredCountries();
      if (filteredCountries.length > 0) {
        rbacHandleCountryCodeSelect(filteredCountries[0].dialCode);
      }
    }
  };

  const canSubmit = () => {
    const isValid = isFormValid();
    // For new admin, just check validation
    if (!admin) return isValid;
    // For editing admin, check both validation and if data has changed
    return isValid && hasFormDataChanged();
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '40%',
    maxWidth: '90vw',
    maxHeight: '98vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '12px',
    fontFamily: 'Poppins',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="rbac-admin-modal-title"
      aria-describedby="rbac-admin-modal-description"
    >
      <Box sx={modalStyle}>
        <Box className="flex items-center justify-between px-6 py-4 border-b border-[#D5DEE3]">
          <Typography className="rbac-modal-title">
            {admin ? 'Edit Admin' : 'Add New Admin'}
          </Typography>
          <IconButton onClick={onClose}>
            <img src={closeIcon} alt="close" />
          </IconButton>
        </Box>
        <Box className="rbac-modal-content">
          <Box className="flex flex-col gap-2">
            <Box className="rbac-field-group">
              <TextField
                label="Full Name"
                value={formData.name}
                onChange={e => handleChange('name', e.target.value)}
                onBlur={() => setTouched(prev => ({ ...prev, name: true }))}
                error={shouldShowError('name')}
                fullWidth
                className="rbac-form-input"
                variant="outlined"
              />
              {shouldShowError('name') && (
                <Typography className="error-text">{errors.name}</Typography>
              )}
            </Box>

            <Box className="rbac-field-group">
              <TextField
                label="Email"
                type="text"
                value={formData.email}
                onChange={e => handleChange('email', e.target.value)}
                onBlur={() => setTouched(prev => ({ ...prev, email: true }))}
                error={shouldShowError('email')}
                fullWidth
                className="rbac-form-input"
                variant="outlined"
              />
              {shouldShowError('email') && (
                <Typography className="error-text">{errors.email}</Typography>
              )}
            </Box>

            <Box className="rbac-field-group">
              <Typography>Phone Number</Typography>
              <Box className="rbac-phone-input-container">
                <div
                  className={`rbac-country-code-dropdown-container ${countryCodeDropdownOpen ? 'open' : ''}`}
                  ref={countryCodeDropdownRef}
                >
                  <div
                    className="rbac-country-code-display-button"
                    onClick={handleCountryCodeDropdownToggle}
                  >
                    <span>{formData.phone_country_code}</span>
                    <span className="rbac-dropdown-arrow">▼</span>
                  </div>
                  {countryCodeDropdownOpen && (
                    <div className="rbac-country-code-dropdown">
                      <div className="rbac-country-code-search-container">
                        <input
                          ref={countryCodeSearchRef}
                          type="text"
                          placeholder="Search countries..."
                          value={countryCodeSearch}
                          onChange={e => setCountryCodeSearch(e.target.value)}
                          className="rbac-country-code-search-input"
                          onKeyDown={handleSearchKeyDown}
                        />
                      </div>
                      <div className="rbac-country-code-dropdown-content">
                        {rbacGetFilteredCountries().length > 0 ? (
                          rbacGetFilteredCountries().map((country, index) => (
                            <div
                              key={`${country.dialCode}-${country.countryCode}-${index}`}
                              className={`rbac-country-code-option ${formData.phone_country_code === country.dialCode ? 'selected' : ''}`}
                              onClick={() => rbacHandleCountryCodeSelect(country.dialCode)}
                            >
                              {country.displayText}
                            </div>
                          ))
                        ) : (
                          <div className="rbac-country-code-option">No countries found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <input
                  type="tel"
                  value={formData.phone_number_only}
                  onChange={e => handleChange('phone_number_only', e.target.value)}
                  onBlur={() => setTouched(prev => ({ ...prev, phone: true }))}
                  className="rbac-phone-number-input"
                  placeholder="Enter phone number"
                  maxLength={15}
                />
              </Box>
              {shouldShowError('phone') && (
                <Typography className="error-text">{errors.phone}</Typography>
              )}
            </Box>

            <Box className="rbac-field-group">
              <FormControl
                fullWidth
                error={shouldShowError('role')}
                className="rbac-form-input"
                variant="outlined"
              >
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  onChange={e => handleChange('role', e.target.value)}
                  onBlur={() => setTouched(prev => ({ ...prev, role: true }))}
                  label="Role"
                >
                  {roles
                    .filter(r => r.value !== 'super_admin')
                    .map(role => (
                      <MenuItem key={role.value} value={role.value}>
                        {role.label}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              {shouldShowError('role') && (
                <Typography className="error-text">{errors.role}</Typography>
              )}
            </Box>

            {(formData.role === 'operations_officer' ||
              formData.role === 'organisation_manager') && (
              <Box className="rbac-field-group">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.bankPayoutAccess}
                      onChange={e =>
                        setFormData(prev => ({ ...prev, bankPayoutAccess: e.target.checked }))
                      }
                    />
                  }
                  label="Bank and payout edit access"
                />
                <Typography variant="caption" color="text.secondary">
                  Allow this organization manager to manage bank details and payouts
                </Typography>
              </Box>
            )}

            {formData.role === 'finance_officer' && (
              <Box className="rbac-field-group">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.feeSettingsAccess}
                      onChange={e =>
                        setFormData(prev => ({ ...prev, feeSettingsAccess: e.target.checked }))
                      }
                    />
                  }
                  label="Fee settings & Payout edit access"
                />
                <Typography variant="caption" color="text.secondary">
                  Allow this finance manager to manage fee settings and individual payout
                </Typography>
              </Box>
            )}

            {formData.role === 'compliance_auditor' && (
              <Box className="rbac-field-group">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.downloadPayoutAccess}
                      onChange={e => {
                        const newValue = e.target.checked;
                        console.log('[RBAC] Compliance Auditor checkbox changed:', newValue);
                        console.log('[RBAC] Current formData:', formData);
                        setFormData(prev => {
                          const updated = { ...prev, downloadPayoutAccess: newValue };
                          console.log('[RBAC] Updated formData:', updated);
                          return updated;
                        });
                      }}
                    />
                  }
                  label="Download payout access"
                />
                <Typography variant="caption" color="text.secondary">
                  Allow this compliance auditor to download bank transaction reports
                </Typography>
              </Box>
            )}

            {admin ? (
              <Box className="rbac-field-group">
                <FormControl component="fieldset" className="rbac-form-input">
                  <Typography>Status</Typography>
                  <RadioGroup
                    row
                    value={formData.status}
                    onChange={e => handleChange('status', e.target.value)}
                  >
                    <FormControlLabel value="active" control={<Radio />} label="Active" />
                    <FormControlLabel value="inactive" control={<Radio />} label="Inactive" />
                  </RadioGroup>
                </FormControl>
              </Box>
            ) : (
              <Box className="rbac-field-group">
                <TextField
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={e => handleChange('password', e.target.value)}
                  onBlur={() => setTouched(prev => ({ ...prev, password: true }))}
                  error={shouldShowError('password')}
                  fullWidth
                  className="rbac-form-input"
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {shouldShowError('password') && (
                  <Typography className="error-text">{errors.password}</Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
        <Box className="flex items-center justify-center gap-4 px-6 py-4 border-t border-[#D5DEE3]">
          <button className="rbac-modal-cancel-button" onClick={onClose}>
            Cancel
          </button>
          <button className="rbac-modal-save-button" onClick={handleSave} disabled={!canSubmit()}>
            {admin ? 'Update' : 'Add'} Admin
          </button>
        </Box>
      </Box>
    </Modal>
  );
};

// Delete Confirmation Modal Component
const DeleteConfirmModal = ({ open, onClose, onConfirm, adminName }) => {
  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '30%',
    maxWidth: '90vw',
    maxHeight: '98vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '12px',
    fontFamily: 'Poppins',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="delete-confirm-modal-title"
      aria-describedby="delete-confirm-modal-description"
    >
      <Box sx={modalStyle}>
        <Box className="flex items-center justify-between px-6 py-4 border-b border-[#D5DEE3]">
          <Typography className="rbac-delete-modal-title">Delete Admin</Typography>
          <IconButton onClick={onClose}>
            <img src={closeIcon} alt="close" />
          </IconButton>
        </Box>
        <Box className="flex flex-col items-center justify-center text-center gap-4 px-[11%] py-4 border-b border-[#D5DEE3]">
          <img src={rejectIcon} alt="delete" />
          <Typography className="rbac-delete-modal-content">
            You are about to delete the admin "{adminName}". This action cannot be undone and will
            permanently remove this admin from the system.
          </Typography>
          <Typography className="rbac-delete-modal-note">
            Note: Deleting an admin means they will lose all access immediately and cannot be
            recovered.
          </Typography>
        </Box>
        <Box className="flex items-center justify-center gap-4 px-6 py-4">
          <button className="rbac-modal-cancel-button" onClick={onClose}>
            Cancel
          </button>
          <button className="rbac-modal-delete-button" onClick={onConfirm}>
            Delete Admin
          </button>
        </Box>
      </Box>
    </Modal>
  );
};

export default Rbac;
